import { useEffect } from "react";
import { Link, Navigate } from "react-router-dom";
import {
  ArrowRight,
  ExternalLink,
  HardHat,
  HelpCircle,
  Loader2,
} from "lucide-react";
import { DOCS_URL, APP_NAME, SUPPORT_EMAIL } from "@crewledger/shared/constants";
import { isWorkspaceSetupComplete } from "@crewledger/shared/workspace";
import { useAuth } from "@/features/auth/auth-provider";
import {
  SetupChecklistGrid,
  SetupNextStepBanner,
  SetupProgressBar,
} from "@/features/beta-onboarding/components/setup-checklist-ui";
import { useWorkspaceSetup } from "@/features/beta-onboarding/hooks/use-workspace-setup";
import { PageHeader, InfoBanner } from "@/components/design";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

/** Full guided setup for beta workspaces with no operational data yet. */
export function DashboardSetupPage() {
  const { workspace } = useAuth();
  const { data, loading, error, refresh } = useWorkspaceSetup();

  useEffect(() => {
    document.title = `Welcome to Werkio — ${APP_NAME}`;
  }, []);

  if (workspace && workspace.type !== "BETA") {
    return <Navigate to="/dashboard" replace />;
  }

  if (!loading && data && data.phase !== "empty") {
    return <Navigate to="/dashboard" replace />;
  }

  if (loading && !data) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Welcome to Werkio"
          description="Your workspace is ready. Complete these steps to start tracking your first pay period."
        />
        <InfoBanner variant="error" title="Could not load setup checklist">
          {error}
        </InfoBanner>
        <Button variant="outline" onClick={() => void refresh()}>
          Try again
        </Button>
      </div>
    );
  }

  const setup = data!;
  const allComplete = isWorkspaceSetupComplete(setup);

  return (
    <div className="space-y-8">
      <PageHeader
        title="Welcome to Werkio"
        description="Your workspace is ready. Complete these steps to start tracking your first pay period."
        actions={
          allComplete ? (
            <Link to="/dashboard">
              <Button>
                Open command center
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          ) : (
            <Button variant="outline" size="sm" onClick={() => void refresh()} disabled={loading}>
              Refresh progress
            </Button>
          )
        }
      />

      <InfoBanner variant="info">
        This is a real Werkio workspace, not demo data. You can start blank and import your workers
        later.
      </InfoBanner>

      <SetupProgressBar
        completedCount={setup.completedCount}
        totalSteps={setup.totalSteps}
        progressPercent={setup.progressPercent}
      />

      <SetupNextStepBanner setup={setup} />

      {allComplete ? (
        <InfoBanner variant="success" title="Setup complete">
          Your workspace has the essentials in place. Head to the command center for daily
          operations, or keep refining sites, workers, and pay rules.
        </InfoBanner>
      ) : null}

      <SetupChecklistGrid setup={setup} />

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <HelpCircle className="h-5 w-5 text-primary" />
              Need help?
            </CardTitle>
            <CardDescription>
              Werkio support can help with onboarding, pay rules, and your first export.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-3">
            <a href={`mailto:${SUPPORT_EMAIL}`}>
              <Button variant="outline" size="sm">
                Contact {SUPPORT_EMAIL}
              </Button>
            </a>
            <a href={DOCS_URL} target="_blank" rel="noreferrer">
              <Button variant="outline" size="sm">
                Read the docs
                <ExternalLink className="ml-2 h-3.5 w-3.5" />
              </Button>
            </a>
          </CardContent>
        </Card>

        <Card className="border-orange-construction/30 bg-orange-50/20">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <HardHat className="h-5 w-5 text-orange-construction" />
              Want to see examples first?
            </CardTitle>
            <CardDescription>
              Explore the Ridgepoint demo workspace with sample sites, workers, equipment, and pay
              periods — without changing your real data.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Link to="/demo/dashboard">
              <Button variant="outline" size="sm" className="border-orange-construction/40">
                Open demo workspace
                <ExternalLink className="ml-2 h-3.5 w-3.5" />
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
